import React, { useState, useEffect, useRef, useMemo } from 'react';
import { auth } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { FiSearch, FiEye, FiPlus, FiFile, FiDownload, FiUpload, FiAlertCircle, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Table, { Column } from '../../../../components/Table';
import {
    fetchCompanyId,
    fetchBAMSHardware,
    createBAMSHardware,
    updateBAMSHardware,
    deleteBAMSHardware,
    fetchBranches,
    fetchDeployments,
    type BAMSHardware,
    type BAMSHardwareFormData
} from '../service/bamsHardwareService';

export default function ManageBAMSHardware() {
    const [hardware, setHardware] = useState<BAMSHardware[]>([]);
    const [filteredHardware, setFilteredHardware] = useState<BAMSHardware[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [regionFilter, setRegionFilter] = useState('All');
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedHardware, setSelectedHardware] = useState<BAMSHardware | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedHardware, setEditedHardware] = useState<Partial<BAMSHardware> | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [hardwareToDelete, setHardwareToDelete] = useState<BAMSHardware | null>(null);
    const [loading, setLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [branches, setBranches] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [vendorMap, setVendorMap] = useState<Map<string, string>>(new Map());
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const pso = useAppSelector((s) => s.user.pso);
    const designation = (pso?.designation || '').replace(/_/g, ' ').toLowerCase();
    const isIncharge = designation.includes('incharge');
    const isBamsIncharge = designation.includes('bams');
    const isMIS = designation.includes('mis');
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';
    const isPSO = userRole === 'pso' && !isIncharge;
    // Build candidate IDs that a branch.psoId might store for this PSO (memoized to prevent infinite loops)
    const psoIdCandidates: string[] = useMemo(() => [
        ...(pso?.authUid ? [pso.authUid] : []),
        ...(((pso as any)?.psoId) ? [(pso as any).psoId as string] : []),
        ...(uid ? [uid] : []),
    ], [pso?.authUid, (pso as any)?.psoId, uid]);

    // Fetch companyId
    useEffect(() => {
        if (!uid) return;

        const loadCompanyId = async () => {
            try {
                const id = await fetchCompanyId(uid, isBankUser);
                if (id) {
                    setCompanyId(id);
                }
            } catch (err) {
                console.error('Error fetching company ID:', err);
            }
        };

        loadCompanyId();
    }, [uid, isBankUser]);

    // Fetch branches
    useEffect(() => {
        if (!companyId) return;

        const loadBranches = async () => {
            try {
                const branchesList = await fetchBranches(companyId);
                setBranches(branchesList);
            } catch (err) {
                console.error('Error fetching branches:', err);
            }
        };

        loadBranches();
    }, [companyId]);

    // Fetch hardware and merge with branches
    useEffect(() => {
        if (!companyId || branches.length === 0) return;
        // For regular PSO users, we need psoIdCandidates, but for Incharge BAMS, MIS, or Bank users, we don't
        if (!isBankUser && !isBamsIncharge && !isMIS && psoIdCandidates.length === 0) return;

        const loadHardware = async () => {
            setLoading(true);
            try {
                const hardwareList = await fetchBAMSHardware(companyId);
                // Fetch ALL deployments with BAMS vendor names (not filtered by PSO) so we can show vendors for all branches
                // Pass fetchAllDeployments=true to fetchDeployments to get all deployments regardless of PSO
                const fetchedVendorMap = await fetchDeployments(companyId, uid || '', true);

                setVendorMap(fetchedVendorMap);
                setHardware(hardwareList);

                // Filter branches to only show those assigned to this PSO (unless bank user, BAMS incharge, or MIS)
                const assignedBranches = branches.filter((branch: any) => {
                    if (isBankUser || isBamsIncharge || isMIS) return true;
                    // If branch has a psoId, it must match one of the current user's PSO IDs
                    if (branch.psoId) {
                        return psoIdCandidates.includes(branch.psoId);
                    }
                    // If branch has no psoId, show it (unassigned branches)
                    return true;
                });

                // Merge branches with hardware data
                // Create a map of hardware by branchId
                const hardwareMap = new Map();
                hardwareList.forEach(hw => {
                    if (hw.branchId) {
                        hardwareMap.set(hw.branchId, hw);
                    }
                });

                // Create merged list: assigned branches with their hardware data (or empty)
                const mergedList = assignedBranches.map(branch => {
                    const existingHardware = hardwareMap.get(branch.id);
                    // Get vendor name from deployment vendors (vendorMap contains all deployments with BAMS companies)
                    const vendorNameFromDeployment = (fetchedVendorMap && fetchedVendorMap.get) ? fetchedVendorMap.get(branch.id) || '' : '';
                    const regionName = branch.locationName || '';

                    if (existingHardware) {
                        // Prefer vendor from deployment vendors, fallback to hardware vendor field
                        const finalVendorName = vendorNameFromDeployment || (existingHardware as any).vendorName || '';
                        return {
                            ...existingHardware,
                            vendorName: finalVendorName,
                            regionName: regionName || (existingHardware as any).regionName || '',
                        };
                    }
                    // Create empty hardware entry for branch without hardware data
                    return {
                        id: `new-${branch.id}`,
                        branchId: branch.id,
                        branchCode: branch.branchCode || '',
                        branchName: branch.address || '',
                        region: '',
                        vendorName: vendorNameFromDeployment,
                        regionName: regionName,
                        response: undefined,
                        noOfBamsSystem: 0,
                        fixedPanicButton: 0,
                        remotePanic: 0,
                        fixedFootPanic: 0,
                        pir: 0,
                        openingDoorContact: 0,
                        smokeHeatDetectors: 0,
                        noHooterSounderBranch: 0,
                        noOfKeypad: 0,
                        cablesWires: 0,
                        other: 0,
                        installationStatus: 'pending' as const,
                        installationDate: '',
                        location: '',
                        remarks: '',
                        companyId: companyId,
                    } as BAMSHardware & { regionName?: string };
                });

                setFilteredHardware(mergedList);
            } catch (err) {
                console.error(err);
                setToastMessage('Failed to load BAMS hardware');
                setToastType('error');
                setShowToast(true);
            } finally {
                setLoading(false);
            }
        };

        loadHardware();
    }, [companyId, branches, isBankUser, isBamsIncharge, isMIS, psoIdCandidates.length]);

    // Filter hardware
    useEffect(() => {
        if (!companyId || branches.length === 0) return;
        // For regular PSO users, we need psoIdCandidates, but for Incharge BAMS, MIS, or Bank users, we don't
        if (!isBankUser && !isBamsIncharge && !isMIS && psoIdCandidates.length === 0) return;

        // Filter branches to only show those assigned to this PSO (unless bank user, BAMS incharge, or MIS)
        const assignedBranches = branches.filter((branch: any) => {
            if (isBankUser || isBamsIncharge || isMIS) return true;
            // Only show branches assigned to this PSO
            if (branch.psoId && !psoIdCandidates.includes(branch.psoId)) {
                return false;
            }
            return true;
        });

        // Merge branches with hardware data
        const hardwareMap = new Map();
        hardware.forEach(hw => {
            if (hw.branchId) {
                hardwareMap.set(hw.branchId, hw);
            }
        });

        let list = assignedBranches.map(branch => {
            const existingHardware = hardwareMap.get(branch.id);
            // Get vendor name from deployment vendors (vendorMap contains all deployments with BAMS companies)
            const vendorNameFromDeployment = (vendorMap && vendorMap.get) ? vendorMap.get(branch.id) || '' : '';
            const regionName = branch.locationName || '';

            if (existingHardware) {
                // Prefer vendor from deployment vendors, fallback to hardware vendor field
                const finalVendorName = vendorNameFromDeployment || (existingHardware as any).vendorName || '';
                return {
                    ...existingHardware,
                    vendorName: finalVendorName,
                    regionName: regionName || (existingHardware as any).regionName || '',
                };
            }
            return {
                id: `new-${branch.id}`,
                branchId: branch.id,
                branchCode: branch.branchCode || '',
                branchName: branch.address || '',
                region: '',
                vendorName: vendorNameFromDeployment,
                regionName: regionName,
                response: undefined,
                noOfBamsSystem: 0,
                fixedPanicButton: 0,
                remotePanic: 0,
                fixedFootPanic: 0,
                pir: 0,
                openingDoorContact: 0,
                smokeHeatDetectors: 0,
                noHooterSounderBranch: 0,
                noOfKeypad: 0,
                cablesWires: 0,
                other: 0,
                installationStatus: 'pending' as const,
                installationDate: '',
                location: '',
                remarks: '',
                companyId: companyId,
            } as BAMSHardware & { regionName?: string };
        });

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(item => {
                const branchCode = (item.branchCode || '').toString().toLowerCase();
                const branchName = (item.branchName || '').toString().toLowerCase();
                const regionName = ((item as any).regionName || item.region || '').toString().toLowerCase();
                return branchCode.includes(term) || branchName.includes(term) || regionName.includes(term);
            });
        }

        // Apply region filter
        if (regionFilter !== 'All') {
            list = list.filter(item => {
                const itemRegion = ((item as any).regionName || item.region || '').toString().toLowerCase();
                return itemRegion === regionFilter.toLowerCase();
            });
        }

        setFilteredHardware(list);
        setCurrentPage(1); // Reset to first page when filters change
    }, [hardware, branches, searchTerm, regionFilter, vendorMap, companyId, isBankUser, isBamsIncharge, isMIS, psoIdCandidates]);

    const totalPages = Math.ceil(filteredHardware.length / perPage);
    const paginatedData = filteredHardware.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Format date
    const formatDate = (date: any) => {
        if (!date) return 'N/A';
        if (date.toDate) {
            return date.toDate().toLocaleDateString();
        }
        if (date.seconds) {
            return new Date(date.seconds * 1000).toLocaleDateString();
        }
        if (typeof date === 'string') {
            return new Date(date).toLocaleDateString();
        }
        return new Date(date).toLocaleDateString();
    };

    // Get status badge
    const getStatusBadge = (status?: string) => {
        if (!status) return null;

        const statusLower = status.toLowerCase();
        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (statusLower === 'installed') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (statusLower === 'sent') {
            bgColor = 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
        } else if (statusLower === 'pending') {
            bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {status}
            </span>
        );
    };

    // Export to Excel
    const exportToExcel = () => {
        if (filteredHardware.length === 0) {
            setToastMessage('No hardware to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const worksheetData = filteredHardware.map(item => ({
            'Branch/Premises Code': item.branchCode || 'N/A',
            'Branch/Premises Name': item.branchName || 'N/A',
            'Region': (item as any).regionName || item.region || 'N/A',
            'BAMS Vendor Name': item.vendorName || 'N/A',
            'Response': item.response || 'N/A',
            'BAMS system': item.noOfBamsSystem || 0,
            'Fixed Hand Panic': item.fixedPanicButton || 0,
            'Remote Panic': item.remotePanic || 0,
            'Fixed Foot Panic': item.fixedFootPanic || 0,
            'PIR/ Motion sensor': item.pir || 0,
            'Opening door contact': item.openingDoorContact || 0,
            'Smoke Detector': item.smokeHeatDetectors || 0,
            'Hooter/ Sounder': item.noHooterSounderBranch || 0,
            'Keypad': item.noOfKeypad || 0,
            'Cables/ Wires': item.cablesWires || 0,
            'Other': item.other || 0,
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'BAMS Hardware');

        const fileName = `BAMS_Hardware_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        setToastMessage('Excel file exported successfully');
        setToastType('success');
        setShowToast(true);
    };

    const exportToPDF = () => {
        if (filteredHardware.length === 0) {
            setToastMessage('No hardware to export');
            setToastType('warning');
            setShowToast(true);
            return;
        }

        const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
        doc.setFontSize(20);
        doc.text('BAMS Hardware Inventory', 14, 15);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

        const tableData = filteredHardware.map(item => [
            item.branchCode || 'N/A',
            item.branchName || 'N/A',
            (item as any).regionName || item.region || 'N/A',
            item.vendorName || 'N/A',
            item.response || 'N/A',
            item.noOfBamsSystem || 0,
            item.fixedPanicButton || 0,
            item.pir || 0,
            item.noHooterSounderBranch || 0,
            item.installationStatus || 'N/A',
            item.installationDate || 'N/A'
        ]);

        autoTable(doc, {
            head: [['Code', 'Branch Name', 'Region', 'Vendor', 'Resp.', 'Sys', 'Panic', 'PIR', 'Hooter', 'Status', 'Date']],
            body: tableData,
            startY: 30,
            styles: { fontSize: 7, cellPadding: 2 },
            headStyles: { fillColor: [52, 152, 219] },
            alternateRowStyles: { fillColor: [245, 245, 245] }
        });

        const fileName = `BAMS_Hardware_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        setToastMessage('PDF report generated successfully');
        setToastType('success');
        setShowToast(true);
    };

    // Import from Excel
    const handleImportExcel = () => {
        fileInputRef.current?.click();
    };

    const processExcelFile = async (file: File) => {
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (!companyId) {
                setToastMessage('Company ID not found');
                setToastType('error');
                setShowToast(true);
                return;
            }

            let successCount = 0;
            let errorCount = 0;

            for (const row of jsonData as any[]) {
                try {
                    // Find branch by code
                    const branch = branches.find(b => b.branchCode === row['Branch Code']);
                    if (!branch) {
                        errorCount++;
                        continue;
                    }

                    const hardwareData: Partial<BAMSHardware> = {
                        branchId: branch.id,
                        itemCode: row['Item Code'] || '',
                        itemName: row['Item Name'] || '',
                        itemDescription: row['Item Description'] || '',
                        quantity: parseInt(row['Quantity']) || 1,
                        brandName: row['Brand'] || '',
                        makeModel: row['Make/Model'] || '',
                        serialNumber: row['Serial Number'] || '',
                        installationStatus: row['Installation Status']?.toLowerCase() || 'pending',
                        installationDate: row['Installation Date'] || new Date().toISOString().split('T')[0],
                        location: row['Location'] || '',
                        remarks: row['Remarks'] || '',
                    };

                    await createBAMSHardware(companyId, hardwareData);
                    successCount++;
                } catch (err) {
                    console.error('Error importing row:', err);
                    errorCount++;
                }
            }

            setToastMessage(`Import completed: ${successCount} successful, ${errorCount} errors`);
            setToastType(successCount > 0 ? 'success' : 'error');
            setShowToast(true);

            // Refresh hardware list
            const hardwareList = await fetchBAMSHardware(companyId);
            setHardware(hardwareList);
            setFilteredHardware(hardwareList);
        } catch (err) {
            console.error('Error processing Excel file:', err);
            setToastMessage('Failed to import Excel file');
            setToastType('error');
            setShowToast(true);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            processExcelFile(file);
        }
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Form data
    const [formData, setFormData] = useState<Partial<BAMSHardwareFormData>>({
        branchId: '',
        region: '',
        vendorName: '',
        response: undefined,
        noOfBamsSystem: 0,
        fixedPanicButton: 0,
        remotePanic: 0,
        fixedFootPanic: 0,
        pir: 0,
        openingDoorContact: 0,
        smokeHeatDetectors: 0,
        noHooterSounderBranch: 0,
        noOfKeypad: 0,
        cablesWires: 0,
        other: 0,
        installationDate: new Date().toISOString().split('T')[0],
        installationStatus: 'pending',
        location: '',
        remarks: '',
    });

    // Reset form
    const resetForm = () => {
        setFormData({
            branchId: '',
            region: '',
            vendorName: '',
            response: undefined,
            noOfBamsSystem: 0,
            fixedPanicButton: 0,
            remotePanic: 0,
            fixedFootPanic: 0,
            pir: 0,
            openingDoorContact: 0,
            smokeHeatDetectors: 0,
            noHooterSounderBranch: 0,
            noOfKeypad: 0,
            cablesWires: 0,
            other: 0,
            installationDate: new Date().toISOString().split('T')[0],
            installationStatus: 'pending',
            location: '',
            remarks: '',
        });
    };

    // Handle add hardware
    const handleAddHardware = async () => {
        if (!companyId || !uid) {
            setToastMessage('Company ID or User ID not found');
            setToastType('error');
            setShowToast(true);
            return;
        }

        if (!formData.branchId) {
            setToastMessage('Please select a branch');
            setToastType('error');
            setShowToast(true);
            return;
        }

        setIsSubmitting(true);
        try {
            await createBAMSHardware(companyId, formData as Partial<BAMSHardware>);

            setToastMessage('BAMS hardware added successfully');
            setToastType('success');
            setShowToast(true);
            setShowAddModal(false);
            resetForm();

            // Refresh hardware list
            const hardwareList = await fetchBAMSHardware(companyId);
            setHardware(hardwareList);
            setFilteredHardware(hardwareList);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to add BAMS hardware');
            setToastType('error');
            setShowToast(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle view details
    const handleViewDetails = (item: BAMSHardware) => {
        setSelectedHardware(item);
        setEditedHardware({ ...item });
        setIsEditing(false);
        setShowDetailsModal(true);
    };

    // Handle edit
    const handleEdit = () => {
        if (selectedHardware) {
            setIsEditing(true);
            setEditedHardware({ ...selectedHardware });
        }
    };

    // Handle delete
    const handleDeleteClick = (item: BAMSHardware) => {
        setHardwareToDelete(item);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!companyId || !hardwareToDelete) return;

        setDeleting(true);
        try {
            await deleteBAMSHardware(companyId, hardwareToDelete.id);

            setToastMessage('Hardware deleted successfully');
            setToastType('success');
            setShowToast(true);

            // Refresh hardware list
            const hardwareList = await fetchBAMSHardware(companyId);
            setHardware(hardwareList);
            setFilteredHardware(hardwareList);

            if (selectedHardware?.id === hardwareToDelete.id) {
                setShowDetailsModal(false);
                setSelectedHardware(null);
            }

            setShowDeleteModal(false);
            setHardwareToDelete(null);
        } catch (err) {
            console.error(err);
            setToastMessage('Failed to delete hardware');
            setToastType('error');
            setShowToast(true);
        } finally {
            setDeleting(false);
        }
    };

    // Calculate summary statistics

    const columns = useMemo<Column<BAMSHardware & { regionName?: string }>[]>(() => [
        {
            header: 'Branch Code',
            accessor: 'branchCode',
            render: (item) => (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {item.branchCode || 'N/A'}
                </span>
            ),
        },
        {
            header: 'Branch Name',
            accessor: 'branchName',
            className: 'text-gray-600 dark:text-gray-400',
            render: (item) => (
                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '150px' }}>
                    {item.branchName || 'N/A'}
                </div>
            )
        },
        {
            header: 'Region',
            accessor: 'region',
            className: 'text-gray-600 dark:text-gray-400',
            render: (item) => (item as any).regionName || item.region || 'N/A'
        },
        {
            header: 'Vendor',
            accessor: 'vendorName',
            className: 'font-medium text-gray-900 dark:text-gray-100',
            render: (item) => (
                <div style={{ wordWrap: 'break-word', whiteSpace: 'normal', maxWidth: '120px' }}>
                    {item.vendorName || 'N/A'}
                </div>
            )
        },
        {
            header: 'Response',
            accessor: 'response',
            align: 'center',
            className: 'text-gray-600 dark:text-gray-400',
            render: (item) => item.response || 'N/A'
        },
        { header: 'BAMS', accessor: 'noOfBamsSystem', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.noOfBamsSystem || 0 },
        { header: 'Hand Panic', accessor: 'fixedPanicButton', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.fixedPanicButton || 0 },
        { header: 'Remote', accessor: 'remotePanic', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.remotePanic || 0 },
        { header: 'Foot Panic', accessor: 'fixedFootPanic', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.fixedFootPanic || 0 },
        { header: 'PIR', accessor: 'pir', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.pir || 0 },
        { header: 'Door', accessor: 'openingDoorContact', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.openingDoorContact || 0 },
        { header: 'Smoke', accessor: 'smokeHeatDetectors', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.smokeHeatDetectors || 0 },
        { header: 'Hooter', accessor: 'noHooterSounderBranch', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.noHooterSounderBranch || 0 },
        { header: 'Keypad', accessor: 'noOfKeypad', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.noOfKeypad || 0 },
        { header: 'Cables', accessor: 'cablesWires', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.cablesWires || 0 },
        { header: 'Other', accessor: 'other', align: 'center', className: 'text-gray-600 dark:text-gray-400', render: (item) => item.other || 0 },
        {
            header: 'Actions',
            accessor: 'id',
            hidden: !isPSO,
            align: 'center',
            render: (item) => (
                <div className="flex gap-2 justify-center">
                    <button
                        onClick={() => handleViewDetails(item)}
                        className="inline-flex items-center justify-center rounded border border-gray-300 bg-white px-2 py-1.5 text-sm font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                        title="View Details"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    <button
                        onClick={() => {
                            setSelectedHardware(item);
                            setEditedHardware({ ...item });
                            setIsEditing(true);
                            setShowDetailsModal(true);
                        }}
                        className="inline-flex items-center justify-center rounded border border-gray-300 bg-white px-2 py-1.5 text-sm font-medium text-gray-700 transition-all hover:border-green-500 hover:bg-green-50 hover:text-green-600 focus:outline-none focus:ring-1 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-400"
                        title="Edit"
                    >
                        <Icon icon={FiEdit2} className="h-3 w-3" />
                    </button>
                </div>
            )
        }
    ], [isPSO]);

    const headerActions = (
        <div className="flex gap-3">
            <Button variant="secondary" onClick={handleImportExcel} className="flex items-center gap-2">
                <Icon icon={FiUpload} className="h-4 w-4" />
                Import Excel
            </Button>
            <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2">
                <Icon icon={FiFile} className="h-4 w-4" />
                Export Excel
            </Button>
            <Button variant="secondary" onClick={exportToPDF} className="flex items-center gap-2">
                <Icon icon={FiDownload} className="h-4 w-4" />
                Export PDF
            </Button>
        </div>
    );

    const filters = (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
                <Icon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                />
                <input
                    type="text"
                    placeholder="Search by branch code, address, region, vendor name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                />
            </div>
            <div className="relative">
                <select
                    value={regionFilter}
                    onChange={(e) => setRegionFilter(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                >
                    <option value="All">All Regions</option>
                    {Array.from(new Set(
                        branches
                            .filter((branch: any) => {
                                if (isBankUser || isBamsIncharge || isMIS) return true;
                                if (branch.psoId && !psoIdCandidates.includes(branch.psoId)) {
                                    return false;
                                }
                                return true;
                            })
                            .map((branch: any) => branch.locationName || '')
                            .filter((region: string) => region.trim() !== '')
                    ))
                        .sort((a, b) => a.localeCompare(b))
                        .map((region) => (
                            <option key={region} value={region}>{region}</option>
                        ))}
                </select>
            </div>
            {(searchTerm || regionFilter !== 'All') && (
                <Button
                    variant="secondary"
                    onClick={() => {
                        setSearchTerm('');
                        setRegionFilter('All');
                        setCurrentPage(1);
                    }}
                    className="text-sm whitespace-nowrap"
                >
                    Clear Search
                </Button>
            )}
        </div>
    );

    return (
        <>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
            />
            <div className="p-6">
                {/* Toast */}
                {showToast && (
                    <div className="pointer-events-none fixed right-4 top-4 z-50">
                        <div className="pointer-events-auto">
                            <Toast
                                message={toastMessage}
                                type={toastType}
                                onClose={() => setShowToast(false)}
                            />
                        </div>
                    </div>
                )}

                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage="No BAMS hardware available"
                    headerActions={headerActions}
                    filters={filters}
                    title="Manage BAMS Hardware"
                    description="Track BAMS hardware installations and shipments"
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                />







            </div>

            {/* Add Hardware Modal */}
            {showAddModal && (
                <Modal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        resetForm();
                    }}
                    title="Add BAMS Hardware"
                    size="lg"
                >
                    <div className="p-6">
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            handleAddHardware();
                        }}>
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Branch <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={formData.branchId || ''}
                                            onChange={(e) => {
                                                const selectedBranch = branches.find(b => b.id === e.target.value);
                                                setFormData({
                                                    ...formData,
                                                    branchId: e.target.value,
                                                    region: selectedBranch?.locationName || ''
                                                });
                                            }}
                                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            required
                                        >
                                            <option value="">Select Branch</option>
                                            {branches
                                                .filter((branch: any) => {
                                                    if (isBankUser || isBamsIncharge || isMIS) return true;
                                                    // Only show branches assigned to this PSO
                                                    if (branch.psoId && !psoIdCandidates.includes(branch.psoId)) {
                                                        return false;
                                                    }
                                                    return true;
                                                })
                                                .map((branch) => (
                                                    <option key={branch.id} value={branch.id}>{branch.branchCode} - {branch.address}</option>
                                                ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Installation Status <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={formData.installationStatus || 'pending'}
                                            onChange={(e) => setFormData({ ...formData, installationStatus: e.target.value as any })}
                                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            required
                                        >
                                            <option value="pending">Pending</option>
                                            <option value="sent">Sent</option>
                                            <option value="installed">Installed</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Region
                                        </label>
                                        <TextField
                                            type="text"
                                            value={formData.region || ''}
                                            onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                                            placeholder="Enter region"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            BAMS Vendor Name
                                        </label>
                                        <TextField
                                            type="text"
                                            value={formData.vendorName || ''}
                                            onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                                            placeholder="Enter vendor name"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Response
                                    </label>
                                    <select
                                        value={formData.response || ''}
                                        onChange={(e) => setFormData({ ...formData, response: e.target.value as 'Police' | 'QRF' || undefined })}
                                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                    >
                                        <option value="">Select Response</option>
                                        <option value="Police">Police</option>
                                        <option value="QRF">QRF</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        No. of BAMS System
                                    </label>
                                    <TextField
                                        type="number"
                                        value={formData.noOfBamsSystem || 0}
                                        onChange={(e) => setFormData({ ...formData, noOfBamsSystem: parseInt(e.target.value) || 0 })}
                                        min="0"
                                        placeholder="0"
                                    />
                                </div>

                                <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                                    <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Panic Buttons</h3>
                                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Fixed Hand Panic
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.fixedPanicButton || 0}
                                                onChange={(e) => setFormData({ ...formData, fixedPanicButton: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Remote Panic
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.remotePanic || 0}
                                                onChange={(e) => setFormData({ ...formData, remotePanic: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Fixed Foot Panic
                                            </label>
                                            <TextField
                                                type="number"
                                                value={formData.fixedFootPanic || 0}
                                                onChange={(e) => setFormData({ ...formData, fixedFootPanic: parseInt(e.target.value) || 0 })}
                                                min="0"
                                                placeholder="0"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            PIR/ Motion sensor
                                        </label>
                                        <TextField
                                            type="number"
                                            value={formData.pir || 0}
                                            onChange={(e) => setFormData({ ...formData, pir: parseInt(e.target.value) || 0 })}
                                            min="0"
                                            placeholder="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Opening door contact
                                        </label>
                                        <TextField
                                            type="number"
                                            value={formData.openingDoorContact || 0}
                                            onChange={(e) => setFormData({ ...formData, openingDoorContact: parseInt(e.target.value) || 0 })}
                                            min="0"
                                            placeholder="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Smoke Detector
                                        </label>
                                        <TextField
                                            type="number"
                                            value={formData.smokeHeatDetectors || 0}
                                            onChange={(e) => setFormData({ ...formData, smokeHeatDetectors: parseInt(e.target.value) || 0 })}
                                            min="0"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Hooter/ Sounder
                                    </label>
                                    <TextField
                                        type="number"
                                        value={formData.noHooterSounderBranch || 0}
                                        onChange={(e) => setFormData({ ...formData, noHooterSounderBranch: parseInt(e.target.value) || 0 })}
                                        min="0"
                                        placeholder="0"
                                    />
                                </div>

                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Keypad
                                    </label>
                                    <TextField
                                        type="number"
                                        value={formData.noOfKeypad || 0}
                                        onChange={(e) => setFormData({ ...formData, noOfKeypad: parseInt(e.target.value) || 0 })}
                                        min="0"
                                        placeholder="0"
                                    />
                                </div>

                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Cables/ Wires
                                        </label>
                                        <TextField
                                            type="number"
                                            value={formData.cablesWires || 0}
                                            onChange={(e) => setFormData({ ...formData, cablesWires: parseInt(e.target.value) || 0 })}
                                            min="0"
                                            placeholder="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Other
                                        </label>
                                        <TextField
                                            type="number"
                                            value={formData.other || 0}
                                            onChange={(e) => setFormData({ ...formData, other: parseInt(e.target.value) || 0 })}
                                            min="0"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Installation Date
                                        </label>
                                        <TextField
                                            type="date"
                                            value={formData.installationDate || ''}
                                            onChange={(e) => setFormData({ ...formData, installationDate: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Location
                                        </label>
                                        <TextField
                                            type="text"
                                            value={formData.location || ''}
                                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                            placeholder="Enter location"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Remarks
                                    </label>
                                    <textarea
                                        value={formData.remarks || ''}
                                        onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                        rows={3}
                                        placeholder="Enter remarks"
                                    />
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setShowAddModal(false);
                                        resetForm();
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Adding...' : 'Add Hardware'}
                                </Button>
                            </div>
                        </form>
                    </div>
                </Modal>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedHardware && editedHardware && (
                <Modal
                    isOpen={showDetailsModal}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedHardware(null);
                        setIsEditing(false);
                        setEditedHardware(null);
                    }}
                    title={`Hardware Details - ${selectedHardware.branchCode || selectedHardware.id}`}
                    size="lg"
                >
                    <div className="p-6">
                        {!isEditing && (
                            <div className="mb-4 flex justify-end">
                                <Button
                                    variant="secondary"
                                    onClick={handleEdit}
                                    className="flex items-center gap-2"
                                >
                                    <Icon icon={FiEdit2} className="h-4 w-4" />
                                    Edit
                                </Button>
                            </div>
                        )}

                        <div className="space-y-4">
                            {/* Row 1: Branch/Premises Code, Branch/Premises Name */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch/Premises Code</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.branchCode || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch/Premises Name</label>
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.branchName || 'N/A'}</p>
                                </div>
                            </div>

                            {/* Row 2: Region */}
                            <div>
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Region</label>
                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{(selectedHardware as any).regionName || selectedHardware.region || 'N/A'}</p>
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">(Auto-populated from branch location)</p>
                            </div>

                            {/* Row 3: BAMS Vendor Name */}
                            <div>
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">BAMS Vendor Name</label>
                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.vendorName || 'N/A'}</p>
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">(Auto-populated from deployed BAMS companies)</p>
                            </div>

                            {/* Row 4: Response */}
                            <div>
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Response</label>
                                {isEditing ? (
                                    <select
                                        value={editedHardware.response || ''}
                                        onChange={(e) => setEditedHardware({ ...editedHardware, response: e.target.value as 'Police' | 'QRF' || undefined })}
                                        className="mt-1 w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                    >
                                        <option value="">Select Response</option>
                                        <option value="Police">Police</option>
                                        <option value="QRF">QRF</option>
                                    </select>
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.response || 'N/A'}</p>
                                )}
                            </div>

                            <div>
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">No. of BAMS System</label>
                                {isEditing ? (
                                    <TextField
                                        type="number"
                                        value={editedHardware.noOfBamsSystem || 0}
                                        onChange={(e) => setEditedHardware({ ...editedHardware, noOfBamsSystem: parseInt(e.target.value) || 0 })}
                                        className="mt-1"
                                        min="0"
                                        placeholder="0"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.noOfBamsSystem || 0}</p>
                                )}
                            </div>

                            {/* Row 5: Panic Buttons */}
                            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                                <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Panic Buttons</h3>
                                <div className="grid grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Fixed Hand Panic</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedHardware.fixedPanicButton || 0}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, fixedPanicButton: parseInt(e.target.value) || 0 })}
                                                className="mt-1"
                                                min="0"
                                                placeholder="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.fixedPanicButton || 0}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Remote Panic</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedHardware.remotePanic || 0}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, remotePanic: parseInt(e.target.value) || 0 })}
                                                className="mt-1"
                                                min="0"
                                                placeholder="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.remotePanic || 0}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Fixed Foot Panic</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedHardware.fixedFootPanic || 0}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, fixedFootPanic: parseInt(e.target.value) || 0 })}
                                                className="mt-1"
                                                min="0"
                                                placeholder="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.fixedFootPanic || 0}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Row 6: Detectors & Sensors */}
                            <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                                <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">Detectors & Sensors</h3>
                                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">PIR/ Motion sensor</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedHardware.pir || 0}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, pir: parseInt(e.target.value) || 0 })}
                                                className="mt-1"
                                                min="0"
                                                placeholder="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.pir || 0}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Opening door contact</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedHardware.openingDoorContact || 0}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, openingDoorContact: parseInt(e.target.value) || 0 })}
                                                className="mt-1"
                                                min="0"
                                                placeholder="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.openingDoorContact || 0}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Smoke Detector</label>
                                        {isEditing ? (
                                            <TextField
                                                type="number"
                                                value={editedHardware.smokeHeatDetectors || 0}
                                                onChange={(e) => setEditedHardware({ ...editedHardware, smokeHeatDetectors: parseInt(e.target.value) || 0 })}
                                                className="mt-1"
                                                min="0"
                                                placeholder="0"
                                            />
                                        ) : (
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.smokeHeatDetectors || 0}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Hooter/ Sounder</label>
                                {isEditing ? (
                                    <TextField
                                        type="number"
                                        value={editedHardware.noHooterSounderBranch || 0}
                                        onChange={(e) => setEditedHardware({ ...editedHardware, noHooterSounderBranch: parseInt(e.target.value) || 0 })}
                                        className="mt-1"
                                        min="0"
                                        placeholder="0"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.noHooterSounderBranch || 0}</p>
                                )}
                            </div>

                            <div>
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Keypad</label>
                                {isEditing ? (
                                    <TextField
                                        type="number"
                                        value={editedHardware.noOfKeypad || 0}
                                        onChange={(e) => setEditedHardware({ ...editedHardware, noOfKeypad: parseInt(e.target.value) || 0 })}
                                        className="mt-1"
                                        min="0"
                                        placeholder="0"
                                    />
                                ) : (
                                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.noOfKeypad || 0}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Cables/ Wires</label>
                                    {isEditing ? (
                                        <TextField
                                            type="number"
                                            value={editedHardware.cablesWires || 0}
                                            onChange={(e) => setEditedHardware({ ...editedHardware, cablesWires: parseInt(e.target.value) || 0 })}
                                            className="mt-1"
                                            min="0"
                                            placeholder="0"
                                        />
                                    ) : (
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.cablesWires || 0}</p>
                                    )}
                                </div>
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Other</label>
                                    {isEditing ? (
                                        <TextField
                                            type="number"
                                            value={editedHardware.other || 0}
                                            onChange={(e) => setEditedHardware({ ...editedHardware, other: parseInt(e.target.value) || 0 })}
                                            className="mt-1"
                                            min="0"
                                            placeholder="0"
                                        />
                                    ) : (
                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedHardware.other || 0}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button variant="secondary" onClick={() => {
                                setShowDetailsModal(false);
                                setSelectedHardware(null);
                                setIsEditing(false);
                                setEditedHardware(null);
                            }}>
                                {isEditing ? 'Cancel' : 'Close'}
                            </Button>
                            {isEditing && (
                                <Button
                                    variant="primary"
                                    onClick={async () => {
                                        if (!companyId || !editedHardware || !uid) return;

                                        try {
                                            // Prepare update data - exclude id and computed fields, include branchId
                                            const updateData: Partial<BAMSHardware> = {
                                                ...editedHardware,
                                                branchId: selectedHardware.branchId || editedHardware.branchId,
                                            };
                                            // Remove id and computed fields that shouldn't be saved
                                            // These fields are computed from branches and deployments
                                            delete (updateData as any).id;
                                            delete (updateData as any).regionName;
                                            delete (updateData as any).vendorName; // Vendor comes from deployments
                                            delete (updateData as any).region; // Region comes from branch locationName

                                            // Check if this is a new entry (id starts with "new-")
                                            if (selectedHardware.id.startsWith('new-')) {
                                                // Create new hardware entry
                                                await createBAMSHardware(companyId, updateData);
                                                setToastMessage('Hardware added successfully');
                                            } else {
                                                // Update existing hardware entry
                                                await updateBAMSHardware(companyId, selectedHardware.id, updateData);
                                                setToastMessage('Hardware updated successfully');
                                            }

                                            setToastType('success');
                                            setShowToast(true);
                                            setIsEditing(false);

                                            // Refresh hardware list
                                            const hardwareList = await fetchBAMSHardware(companyId);
                                            setHardware(hardwareList);

                                            // Refresh the merged list (will be done by the filter effect)
                                            setSelectedHardware(null);
                                            setEditedHardware(null);
                                            setShowDetailsModal(false);
                                        } catch (err) {
                                            console.error(err);
                                            setToastMessage('Failed to save hardware');
                                            setToastType('error');
                                            setShowToast(true);
                                        }
                                    }}
                                >
                                    Save Changes
                                </Button>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && hardwareToDelete && (
                <Modal
                    isOpen={showDeleteModal}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setHardwareToDelete(null);
                    }}
                    title="Confirm Deletion"
                    size="md"
                >
                    <div className="p-6">
                        <div className="mb-4 text-center">
                            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                                <Icon icon={FiAlertCircle} className="h-6 w-6 text-red-600 dark:text-red-400" />
                            </div>
                        </div>
                        <div className="text-center">
                            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">Are you sure?</h3>
                            <p className="mb-1 text-sm text-gray-600 dark:text-gray-400">Do you really want to delete this hardware record?</p>
                            <p className="mb-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                                <span className="font-semibold">Branch:</span> {hardwareToDelete.branchCode || 'N/A'}
                                <br />
                                <span className="font-semibold">Item:</span> {hardwareToDelete.itemName || 'N/A'}
                            </p>
                            <p className="text-sm text-red-600 dark:text-red-400">This action cannot be undone.</p>
                        </div>
                        <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setHardwareToDelete(null);
                                }}
                                disabled={deleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
                            >
                                {deleting ? 'Deleting...' : (
                                    <>
                                        <Icon icon={FiTrash2} className="h-4 w-4" />
                                        Delete
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

